import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient.ts';
import type { PropertySensorGroupRow } from '@metron/shared';
import { toErrorMessage } from '@metron/shared';

interface ListSensorGroupsResponse {
  sensorGroups: PropertySensorGroupRow[];
}

const SENSOR_GROUPS_QUERY_KEY = ['sensorGroups'] as const;

/**
 * React Query-based hook for fetching and managing sensor group data
 *
 * Features:
 * - Automatic caching via React Query
 * - Optional customer filtering
 * - Loading and error states
 * - Cache invalidation support
 *
 * @example
 * ```tsx
 * const { sensorGroups, isLoading, error } = useSensorGroups();
 *
 * // With customer filtering
 * const { sensorGroups } = useSensorGroups(customerId);
 * ```
 */
export function useSensorGroups(customerId?: number) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  // Build query key with optional customerId for cache separation
  const queryKey = customerId
    ? [...SENSOR_GROUPS_QUERY_KEY, customerId] as const
    : SENSOR_GROUPS_QUERY_KEY;

  // Fetch sensor groups query with automatic caching
  const {
    data: sensorGroups = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = customerId ? `?customerId=${customerId}` : '';

      const data = await apiClient.get<ListSensorGroupsResponse>(
        `sensor-groups${params}`,
      );
      return data.sensorGroups;
    },
  });

  // Invalidate cache to refetch sensor groups
  const refetchSensorGroups = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  // Filter sensor groups by type (master/parent vs standalone)
  // Master/parent groups have a childSensorGroupId, standalone groups don't
  const getSensorGroupsByType = useCallback((type: 'master' | 'standalone') => {
    if (type === 'master') {
      return sensorGroups.filter((sg) => sg.childSensorGroupId !== null);
    }
    return sensorGroups.filter((sg) => sg.childSensorGroupId === null);
  }, [sensorGroups]);

  /**
   * Get sensor groups eligible for standalone assignment to a property
   *
   * Business rules:
   * - Must be unassigned (propertyId === null)
   * - Cannot be a child in a master-child relationship (parentSensorGroupId === null)
   * - Cannot be a master with a child (childSensorGroupId === null)
   */
  const getEligibleForStandaloneAssignment = useCallback(() => {
    return sensorGroups.filter((sg) =>
      sg.propertyId === null &&
      sg.parentSensorGroupId === null &&
      sg.childSensorGroupId === null
    );
  }, [sensorGroups]);

  /**
   * Get sensor groups eligible for master role assignment
   *
   * Business rules:
   * - Must be unassigned (propertyId === null)
   * - Cannot already have a child (childSensorGroupId === null)
   * - Cannot already have a parent (parentSensorGroupId === null)
   * - Optionally exclude a specific ID (e.g., the selected child to prevent self-reference)
   *
   * @param excludeId - Optional sensor group ID to exclude from results
   */
  const getEligibleForMasterRole = useCallback((excludeId?: number) => {
    return sensorGroups.filter((sg) =>
      sg.propertyId === null &&
      sg.childSensorGroupId === null &&
      sg.parentSensorGroupId === null &&
      (excludeId === undefined || sg.sensorGroupId !== excludeId)
    );
  }, [sensorGroups]);

  /**
   * Get sensor groups eligible for child role assignment
   *
   * Business rules:
   * - Must be unassigned OR assigned to the current property
   * - Cannot already have a parent (parentSensorGroupId === null)
   * - Cannot already have a child (childSensorGroupId === null)
   * - Optionally exclude a specific ID (e.g., the selected master to prevent self-reference)
   *
   * @param propertyId - The property ID to check for current assignment
   * @param excludeId - Optional sensor group ID to exclude from results
   */
  const getEligibleForChildRole = useCallback((propertyId: number, excludeId?: number) => {
    return sensorGroups.filter((sg) =>
      (sg.propertyId === null || sg.propertyId === propertyId) &&
      sg.parentSensorGroupId === null &&
      sg.childSensorGroupId === null &&
      (excludeId === undefined || sg.sensorGroupId !== excludeId)
    );
  }, [sensorGroups]);

  // Error handling - convert query error to string for backward compatibility
  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return {
    sensorGroups,
    isLoading,
    error,
    refetchSensorGroups,
    getSensorGroupsByType,
    getEligibleForStandaloneAssignment,
    getEligibleForMasterRole,
    getEligibleForChildRole,
  };
}
